import { FaroTextFieldMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { LabelWithHelp } from "@components/common/label-with-help";
import { createMemberOption, createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { Stack } from "@mui-wrapper";
import { addGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { useMemo, useState } from "react";
import { Dialog, TextField, Button } from "@flat-ui";
import { STACK_ERROR_SX } from "@components/common/dialog/dialog-constants";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useTeamUtils } from "@hooks/use-team-utils";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { sortTeams } from "@utils/team-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { XgSeatCheckbox } from "@components/table/members/assign-seats/xg-seat-checkbox";
import { useInviteFailuresDialog } from "@components/project/invite-failures-list";
import { useInviteXgSeats } from "@hooks/use-invite-xg-seats";
import { getInviteHelperText, GROUP_XG_SEAT_DESCRIPTION, GROUP_XG_SEAT_REQUIRED_MESSAGE, isMissingXgSeat } from "@utils/invite-xg-seat-utils";

/** The group managers after giving them their Sphere XG seats, see getManagersWithSeats */

/**
 * Renders the create group button and the dialog to be used to create new groups.
 * This component does not take care of the authorization of the user to create groups.
 */
export function CreateGroup({
  groups
}) {
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const companyId = useCompanyId();
  const companyMembers = useCompanyMembers();
  const currentUser = useAppSelector(currentUserSelector);
  const teams = useAppSelector(assignableTeamsSelector);
  const {
    isTeamName
  } = useTeamUtils();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const showFailures = useInviteFailuresDialog();

  // The group managers need a Sphere XG seat on a Scan plan
  const xgSeats = useInviteXgSeats({
    isSeatRequiredForRole: true,
    seatRequiredMessage: GROUP_XG_SEAT_REQUIRED_MESSAGE,
    location: "create-group"
  });
  const {
    memberWithoutSeatDisabledMessage,
    teamDisabledMessage
  } = xgSeats;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /** Stores the name for the group to be created */
  const [groupName, setGroupName] = useState("");

  /** Stores the names of the groups the user has access to. */
  const groupNames = groups.map(group => group.name);

  /** Stores the members of the company in the format required by the autocomplete */
  const memberOptions = useMemo(() => {
    return sortMembersByNameOrEmail(companyMembers).map(member => {
      /**
       * If the user is a group manager, it should be added to the group by default
       * and it should not be removed, otherwise the creator of the group would not have access to it.
       */
      const isPreselected = currentUser?.role === "COMPANY_MANAGER" && currentUser?.identity === member.identity;

      // The creator is kept as manager, see above, while the backend checks their seat
      const disabledMessage = !isPreselected && isMissingXgSeat(member) ? memberWithoutSeatDisabledMessage : undefined;
      return createMemberOption({
        member,
        isPreselected,
        isDisabled: !!disabledMessage,
        disabledMessage: disabledMessage ?? ""
      });
    });
  }, [companyMembers, currentUser, memberWithoutSeatDisabledMessage]);

  /** Reforming team data for autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => createTeamOption({
    team,
    isDisabled: !!teamDisabledMessage,
    disabledMessage: teamDisabledMessage ?? ""
  })), [teams, teamDisabledMessage]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers: selectedManagers,
    setSelectedMembers: setSelectedManagers,
    uniqueSelectedValues: uniqueManagersIds,
    helperText,
    inputHelperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "group",
    existingMembers: [],
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_0 => member_0.identity)
  });

  // Without the manager field, the current user is the manager, who needs no seat to be given
  const xgSeatsSelection = canAssignManagers ? xgSeats.checkSelection(selectedManagers) : undefined;

  /** Stores whether the create button should be disabled */
  const isCreateDisabled = useMemo(() => {
    // Workspaces without the manager feature only pick a group name, so the members
    // field is neither shown nor validated. The current user becomes the implicit
    // manager, so without them there is no manager to send.
    if (!canAssignManagers) {
      return !groupName || !currentUser;
    }
    return !groupName || shouldDisableSendInvite(uniqueManagersIds, isMemberInputValid) || xgSeatsSelection?.isValid === false;
  }, [canAssignManagers, currentUser, groupName, uniqueManagersIds, isMemberInputValid, xgSeatsSelection?.isValid]);

  /** Resets the dialog content, and opens the dialog. */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog() {
    // Start empty, however the dialog was closed last time, e.g. after creating a group
    reset();
    setGroupName("");
    // Add all the preselected members as group managers by default.
    const preselectedManagers = memberOptions.filter(member_1 => member_1.isPreselected);
    setSelectedManagers(preselectedManagers);
    setIsDialogOpen(true);
  }
  function closeDialog() {
    reset();
    setGroupName("");
    setIsDialogOpen(false);
  }

  /**
   * Triggered when the create button is clicked: gives the managers without one their Sphere XG
   * seat, creates the group with those that have one, and closes the dialog
   */
  async function handleConfirm() {
    setIsLoading(true);
    const {
      identities,
      seatFailures,
      areSeatsGiven
    } = await getManagersWithSeats();

    // Without a manager that got their seat, the dialog stays open to choose others
    if (!identities.length) {
      showSeatFailures(seatFailures, false, areSeatsGiven);
      setIsLoading(false);
      return;
    }
    let isCreated = false;
    try {
      await dispatch(addGroup({
        companyId,
        groupName,
        selectedGroupManagerIds: identities
      })).unwrap();
      isCreated = true;
    } catch (error) {
      handleErrorWithToast({
        id: `createGroup-${Date.now().toString()}`,
        title: "Error creating group",
        error
      });
    } finally {
      // Only once the group is created can the other managers be told to be in it
      showSeatFailures(seatFailures, isCreated, areSeatsGiven);
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }

  /** Tells the user about the managers that could not be given their seat, if there are any */
  function showSeatFailures(failures, isAnyInvited, areSeatsGiven_0) {
    if (failures.length) {
      showFailures({
        failures,
        isAnyInvited,
        areSeatsGiven: areSeatsGiven_0
      });
    }
  }

  /**
   * Gives the group managers without one a Sphere XG seat. A seat cannot be taken away again, so
   * the managers keep theirs even when creating the group fails.
   *
   * @returns the identities of the managers that hold a seat then, and those that could not get one
   */
  async function getManagersWithSeats() {
    // When the workspace cannot assign managers the manager field is hidden and the
    // current user (main admin) is used implicitly, since the backend requires at least
    // one group manager. `isCreateDisabled` guarantees the current user is loaded there.
    if (!xgSeatsSelection) {
      return {
        identities: currentUser ? [currentUser.identity] : uniqueManagersIds,
        seatFailures: [],
        areSeatsGiven: false
      };
    }
    const {
      failures: failures_0,
      failedValues,
      numberOfSeatsGiven
    } = await xgSeatsSelection.giveSeats();
    return {
      identities: uniqueManagersIds.filter(value => !failedValues.has(value)),
      seatFailures: failures_0,
      areSeatsGiven: numberOfSeatsGiven > 0
    };
  }
  return <>
      {/* Button to open the dialog */}
      <Button onClick={onOpenDialog} sx={{
      width: isScreenSmOrSmaller ? "100%" : "auto"
    }}>
        New group
      </Button>

      {/* Create group dialog */}
      <Dialog title="New group" open={isDialogOpen} confirmText="Create" isConfirmDisabled={isCreateDisabled} onConfirm={handleConfirm} showSpinner={isLoading} onCancel={closeDialog}>
        {/* Group name */}
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group name" isRequired />
          <TextField autoFocus placeholder="Enter group name" fullWidth text={groupName} onTextChanged={setGroupName} />
          <FaroTextFieldMessage shouldHide={!groupNames.includes(groupName)} message={{
          type: "info",
          helperText: "Group name already exists"
        }} />
        </Stack>

        {/* Groups manager - only workspaces that can assign managers pick members here */}
        {canAssignManagers && <Stack sx={STACK_ERROR_SX}>
            <MembersAutocomplete isRequired label="Group manager" helperText={getInviteHelperText({
          inputHelperText,
          selectionHelperText: helperText,
          xgSeatsHelperText: xgSeatsSelection?.helperText
        })} options={autocompleteOptions} onInputChange={onInputChange} selectedOptions={selectedManagers} handleMemberSelect={handleMemberSelect} isValid={value_0 => isNewEmailValid(value_0) || isTeamName(value_0)} getChipColor={xgSeatsSelection?.getChipColor}
        // The seats are given to the managers selected when the button was clicked
        isDisabled={isLoading} />
            {xgSeatsSelection?.giveSeatsCheckbox && <XgSeatCheckbox {...xgSeatsSelection.giveSeatsCheckbox} description={GROUP_XG_SEAT_DESCRIPTION} />}
          </Stack>}
      </Dialog>
    </>;
}